import { apiClient } from './client';
import type {
  Communication,
  CommunicationCreateInput,
  CommunicationPrefillResponse,
  CommunicationUpdateInput,
  Letterhead,
  LetterheadInput,
  LetterTemplateDef,
  LetterRecipient,
} from '../types';

export interface CreateLetterBody {
  template_name: string;
  letterhead_id: number;
  recipient: LetterRecipient;
  variables: Record<string, unknown>;
  property_adresse_id?: number | null;
}

export const communicationsApi = {
  // Letter templates registry
  getLetterTemplates: async (): Promise<LetterTemplateDef[]> => {
    const { data } = await apiClient.get('/communications/letter-templates');
    return data;
  },

  // Letterheads
  getLetterheads: async (): Promise<Letterhead[]> => {
    const { data } = await apiClient.get('/letterheads');
    return data;
  },

  createLetterhead: async (body: LetterheadInput): Promise<Letterhead> => {
    const { data } = await apiClient.post('/letterheads', body);
    return data;
  },

  updateLetterhead: async (id: number, body: Partial<LetterheadInput>): Promise<Letterhead> => {
    const { data } = await apiClient.patch(`/letterheads/${id}`, body);
    return data;
  },

  deleteLetterhead: async (id: number): Promise<void> => {
    await apiClient.delete(`/letterheads/${id}`);
  },

  // Communications per entity
  getCommunications: async (entityType: string, entityId: number): Promise<Communication[]> => {
    const { data } = await apiClient.get(`/${entityType}/${entityId}/communications`);
    return data;
  },

  getCommunication: async (id: number): Promise<Communication> => {
    const { data } = await apiClient.get(`/communications/${id}`);
    return data;
  },

  getPrefill: async (entityType: string, entityId: number): Promise<CommunicationPrefillResponse> => {
    const { data } = await apiClient.get(`/${entityType}/${entityId}/communications/prefill`);
    return data;
  },

  // Generic create / update (calls, emails, non-template letters)
  createCommunication: async (
    entityType: string,
    entityId: number,
    body: CommunicationCreateInput,
  ): Promise<Communication> => {
    const { data } = await apiClient.post(
      `/${entityType}/${entityId}/communications`,
      body,
    );
    return data;
  },

  updateCommunication: async (id: number, body: CommunicationUpdateInput): Promise<Communication> => {
    const { data } = await apiClient.patch(`/communications/${id}`, body);
    return data;
  },

  deleteCommunication: async (id: number): Promise<void> => {
    await apiClient.delete(`/communications/${id}`);
  },

  // Letter wizard
  createLetter: async (
    entityType: string,
    entityId: number,
    body: CreateLetterBody,
  ): Promise<Communication> => {
    const { data } = await apiClient.post(
      `/${entityType}/${entityId}/communications/letter`,
      body,
    );
    return data;
  },

  updateLetterDraft: async (id: number, body: CreateLetterBody): Promise<Communication> => {
    const { data } = await apiClient.put(`/communications/${id}/letter`, body);
    return data;
  },

  finalizeLetter: async (id: number): Promise<Communication> => {
    const { data } = await apiClient.post(`/communications/${id}/finalize`);
    return data;
  },
};
